import React, { useRef, useEffect, useState } from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import FusionChip from '@directv-digital/fusion.atoms.chip';
import DtvTheme from "@directv-digital/dds.ui.dtv-theme";
import Button from '@directv-digital/fusion.atoms.button';
import { UpIcon, DownIcon } from '@directv-digital/fusion.atoms.icons';
import { useLocationHref } from "src/hooks/useLocationHref";

export interface FilterChipsProps {
    selectedChannels: any;
    theming: 'light' | 'dark';
    handleRemoveTag: (tag: { displayText: string }) => void;
}

{ /* Generated by Copilot- Start */ }
const FilterChips = (props: FilterChipsProps) => {
  const locationHrefUrl = useLocationHref();
  const { selectedChannels, theming, handleRemoveTag } = props;
  const contentRef = useRef(null);
  const [showAllTags, setShowAllTags] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [screenType, setScreenType] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  useEffect(() => {
    const handleResize = () => {
      if (window?.innerWidth <= 600) {
        setScreenType('mobile');
      } else if (window?.innerWidth >= 1199) {
        setScreenType('desktop');
      } else {
        setScreenType('tablet');
      }
    };
    handleResize();
    window?.addEventListener?.("resize", handleResize);
    return () => window?.removeEventListener?.("resize", handleResize);
  }, []);
  useEffect(() => {
    const contentDiv = contentRef.current;
    if (contentDiv) {
      // Check if the content height exceeds the visible height
      if ((screenType === 'desktop' && contentDiv.scrollHeight > 160) || ((screenType === 'mobile' || screenType === 'tablet') && contentDiv.scrollHeight > 240)) {
        setShowButton(true);
        setShowAllTags(false);
      } else {
        setShowButton(false);
      }
    }
  }, [selectedChannels, screenType]); // Empty dependency array ensures this runs once after initial render
  return (
        <>
            <DtvBox
            ref={contentRef}
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                width: "100%",
                margin: '20px auto 0px',
                maxHeight: 'calc(3 * 48px)', // 3 rows for desktop
                [DtvTheme.breakpoints.between(DtvTheme.breakpoints.values.sm, DtvTheme.breakpoints.values.md)]: {
                maxHeight: 'calc(5 * 48px)' // 3 rows for desktop
                },
                [DtvTheme.breakpoints.down(DtvTheme.breakpoints.values.sm)]: {
                maxHeight: 'calc(5 * 48px)' // 3 rows for desktop
                },
                ...(showAllTags && {
                maxHeight: 'none !important'
                }),
                overflow: 'hidden'
            }}>
                {selectedChannels.map((tag: any) => {
                    const textLabel = `search - ${selectedChannels
                                .filter(ch => ch?.displayText !== tag?.displayText)
                                .map(ch => ch?.displayText?.toLowerCase())
                                .join(', ')}`
                    return (<FusionChip
                        color="primary"
                        key={tag?.displayText}
                        labelText={tag?.displayText}
                        mode={theming}
                        tabIndex={-1}
                        variant="outlined"
                        deleteIconProps={{
                            "aria-label": `remove ${tag?.displayText?.toLowerCase()}`,
                            "data-linklabel": textLabel,
                            "data-linkposition": "panel_channel lineup",
                            "data-linkdestination": locationHrefUrl
                        }}
                        sx={{
                        "&:hover": {
                        backgroundColor: "transparent !important"
                        },
                        "&.active-chip": {
                        backgroundColor: "transparent !important"
                        },
                        "& .MuiIconButton-root:hover ": {
                        backgroundColor: theming === "dark" ? "#8cd3f514 !important" : "#10264114 !important"
                        },
                        "& .MuiIconButton-root:focus-visible": {
                            outline: `4px solid transparent !important`,
                            outlineColor: `${theming === 'dark' ? '#1fbcf8' : '#3885e1'} !important`,
                            outlineOffset: '0px',
                            borderRadius: "50%"
                        }
                        }}
                        onClick={() => handleRemoveTag(tag)}
                        onDelete={() => handleRemoveTag(tag)}
                        onKeyUp={event => event.key === "Enter" && handleRemoveTag(tag)}
                    />)
                })}
            </DtvBox>
            {showButton && (
                <DtvBox sx={{
 width: "100%",
textAlign: 'center',
margin: '10px auto 0px',
display: 'flex',
alignItems: 'center'
}}>
                    <DtvBox sx={{
 flex: 1,
height: '1px',
backgroundColor: '#E0E0E0',
marginRight: '10px'
}} />
                    <Button
                    buttontype="primary"
                    mode={theming}
                    size="small"
                    rightIcon={showAllTags ? <UpIcon
                        fill={theming === 'dark' ? '#FFFFFF' : '#000000'}
                        /> : <DownIcon
                        fill={theming === 'dark' ? '#FFFFFF' : '#000000'}
                        />}
                    onClick={() => setShowAllTags(!showAllTags)}
                    >
                    {showAllTags ? 'Show Less' : 'Show More'}
                    </Button>
                    <DtvBox sx={{
 flex: 1,
height: '1px',
backgroundColor: '#E0E0E0',
marginLeft: '10px'
}} />
                </DtvBox>
            )}
        </>
  );
};
{ /* Generated by Copilot- End */ }

export const MemoizedFilterChips = React.memo(FilterChips);
